import AdminData from "../Models/AdminData";
import User from "../Models/User";
import CreateUserDto from "../Models/CreateUserDto";
import Store from "../Models/Store";
import CreateStoreDto from "../Models/CreateStoreDto";

/**
 * Service for interacting with admin API endpoints.
 */
class AdminService {
    // Base API URL, safely stripped of trailing slashes
    private static readonly baseUrl: string = (() => {
        if (process.env.NODE_ENV !== "production") {
            return "https://localhost:5001/Admin";
        }

        const origin = window.location.origin.replace(/\/+$/, "");
        return `${origin}/api/Admin`;
    })();

    /**
     * Common fetch wrapper to handle headers and errors
     * @param url Relative endpoint URL
     * @param options Fetch options (method, headers, body, etc.)
     */
    private static async request<T>(url: string, options: RequestInit): Promise<T> {
        const response = await fetch(url, options);

        if (!response.ok) {
            const errorText = await response.text();
            const error = new Error(errorText || `Request failed with status ${response.status}`) as any;
            (error as any).status = response.status;
            throw error;
        }

        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
            return response.json();
        } else {
            return response.blob() as unknown as T;
        }
    }

    /**
     * Fetch Admin data (users, stores)
     * @param token Authorization token
     */
    static async getAdminData(token: string | null): Promise<AdminData> {
        return this.request<AdminData>(`${this.baseUrl}/data`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json"
            },
        });
    }

    static async getUsersPaged(token: string | null, page = 1, pageSize = 15): Promise<{ page: number; pageSize: number; totalItems: number; items: User[]; }> {
        return this.request<{ page: number; pageSize: number; totalItems: number; items: User[]; }>(`${this.baseUrl}/users/paged?page=${page}&pageSize=${pageSize}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json"
            }
        });
    }

    static async getStoresPaged(token: string | null, page = 1, pageSize = 15): Promise<{ page: number; pageSize: number; totalItems: number; items: Store[]; }> {
        return this.request<{ page: number; pageSize: number; totalItems: number; items: Store[]; }>(`${this.baseUrl}/stores/paged?page=${page}&pageSize=${pageSize}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json"
            }
        });
    }
    /**
     * Create a user and attach stores
     * @param token Authorization token
     * @param user New User informations
     */
    static async createUser(token: string | null, user: CreateUserDto) {
        return this.request<User>(`${this.baseUrl}/users`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json",
                "Content-Type": "application/json"
            },
            body: JSON.stringify(user),
        });
    }
    /**
     * Update a user (email, stores)
     * @param token Authorization token
     * @param userId User identifier
     * @param user Updated user values
     */
    static async updateUser(
        token: string | null,
        userId: string,
        user: CreateUserDto
    ) {
        return this.request<User>(`${this.baseUrl}/users/${userId}`, {
            method: "PUT",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json",
                "Content-Type": "application/json"
            },
            body: JSON.stringify(user),
        });
    }
    /**
     * Delete a user
     * @param token Authorization token
     * @param userId User identifier
     */
    static async deleteUser(token: string | null, userId: string) {
        return this.request<void>(`${this.baseUrl}/users/${userId}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json",
            },
        });
    }
    /**
     * Create a Store
     * @param token Authorization token
     * @param store New store
     * @returns The created store
     */
    static async createStore(token: string | null, store: CreateStoreDto) {
        return this.request<Store>(`${this.baseUrl}/stores`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json",
                "Content-Type": "application/json"
            },
            body: JSON.stringify(store),
        });
    }
    /**
     * Update a store
     * @param token Authorization token
     * @param storeId store identifier
     * @param store Updated store
     * @returns the updated store
     */
    static async updateStore(
        token: string | null,
        storeId: string,
        store: CreateStoreDto
    ) {
        return this.request<Store>(`${this.baseUrl}/stores/${storeId}`, {
            method: "PUT",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json",
                "Content-Type": "application/json"
            },
            body: JSON.stringify(store),
        });
    }
    /**
     * Delete a store
     * @param token Authorization token
     * @param storeId store identifier
     */
    static async deleteStore(token: string | null, storeId: string) {
        return this.request<void>(`${this.baseUrl}/stores/${storeId}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json",
            },
        });
    }
}

export default AdminService;
